import CryptoJS from 'crypto-js';
import { nanoid } from 'nanoid';

/**
 * Generate an idempotency key for API requests
 * Format: IK_{timestamp}_{nanoid(16)}
 */
export function generateIdempotencyKey(): string {
  const timestamp = Date.now().toString(36);
  const random = nanoid(16);
  return `IK_${timestamp}_${random}`;
}

/**
 * Calculate Content-MD5 for a file (Base64 encoded MD5 hash)
 * Used for S3 upload integrity validation
 */
export async function calculateContentMD5(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
      const md5 = CryptoJS.MD5(wordArray);
      const base64 = md5.toString(CryptoJS.enc.Base64);
      resolve(base64);
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Generate a request ID for tracing
 * Format: REQ_{timestamp}_{nanoid(12)}
 */
export function generateRequestId(): string {
  const timestamp = Date.now().toString(36);
  const random = nanoid(12);
  return `REQ_${timestamp}_${random}`;
}

/**
 * Simple encryption for localStorage (not cryptographically secure, just obfuscation)
 */
const STORAGE_KEY = 'tremoria_storage_key';

export function encryptStorage(data: string): string {
  return CryptoJS.AES.encrypt(data, STORAGE_KEY).toString();
}

export function decryptStorage(encrypted: string): string | null {
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, STORAGE_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}
